import { useCallback, useEffect, useRef, useState } from 'react'
import type { Job, SubmitArgs } from '@/context/JobCenterContext'
import { isActive, useJobCenter } from '@/context/JobCenterContext'
import type { PriceConfirmInfo } from '@/components/studio'
import { withPromptEdits, type PromptEdits } from '@/lib/promptEdits'

/**
 * 待确认的提交。
 *
 * `args` 允许两种形态：**单个产品传一条**（9 个生成页原本的写法，零改动），
 * **批量表格传一组**（一行产品一条）。这里**不用 `args` / `batch` 两个字段** ——
 * 两个字段就会出现「得先判断这次是哪种形态」的分支，而分支漏一处的表现是
 * 「批量时只提交了第一行」，还不报错。一个字段、在 hook 内归一化成数组，
 * 提交路径只有一条。
 */
export type GeneratePayload = {
  info: PriceConfirmInfo
  args: SubmitArgs | SubmitArgs[]
  /**
   * **附带任务**：与主任务共用这一个决策关口（同一次确认、同一笔总价），
   * 但它们是**各自独立的任务** —— 不同工作流、各自成败、各自计费。
   *
   * 目前唯一的使用方是 `/social-image` 的「图文同出」：一次生成既出图
   * （`social-xhs`）又出配套文案（`copywriting`）。用户的真实场景就是
   * 「发布要图文配套」，拆成两次点击等于让他自己记住「图出完了还要再去配一段」。
   *
   * 四条刻意的选择：
   * ① **不走 `submitBatch`** —— 批次语义是「同一个工作流的 N 行产品」，
   *    右栏会给它聚成一张批次卡并标「批量 · N 个」。图与文案聚成一张卡，
   *    那行数字读起来像「两行产品」，而它们根本不是同一件事。
   * ② **串行提交**（`await` 逐个），与 `submitBatch` 同一个理由：上游网关对并发
   *    提交有限流。`submit` 内部把错误吃进 `state: 'failed'`，所以前一条失败
   *    不会连累后一条。
   * ③ **弹窗里的提示词编辑只回填主任务**。弹窗展示的只有主任务那一份词
   *    （`info.prompt` / `info.shotPrompts`），`edits` 也就只对它有语义；
   *    附带任务的词由发起页从表单派生、没有第二处可编辑入口。
   *    ⚠️ 别顺手把 `edits` 也抄给附带任务 —— 那是**另一份词**，
   *    套上去等于用图的提示词去写文案。
   * ④ **附带任务挂在主任务的本地 id 上**（`attachedTo`，见 `confirm` 里那段）。
   *    2026-10-09 用户口径「这个文案跟图片还是同一个任务比较好吧，分开点开
   *    很奇怪」—— 提交形态一个字没动（仍是两条独立任务、各计各的费、各自可重试），
   *    只是右栏「正在生成」把它们画成**一张卡**、点一次就同时看到进度与产出。
   *    那个 id 只有 `submit` 的返回值给得出，所以挂靠点也只能在这里补。
   */
  also?: SubmitArgs[]
  /**
   * 这次提交成功后**别清工作区**（默认清，前提是 `useGenerateFlow` 开了 `workspace`）。
   *
   * 一个页面有多个提交入口时才需要它：数字人的「成片 / 分镜图 / 配音」、
   * 视频页的「成片 / 分镜参考图」—— 辅助产物的成功**不该**把主表单清掉
   * （生成一张分镜参考图就把刚写好的口播稿 / 分镜表清了，用户只会当成丢数据）。
   */
  keepWorkspace?: boolean
  /**
   * 这一批任务**全部落地**（成功 / 失败都算）后的回调，收到的是那几条 `Job`
   * （已带上各自最终的 `state` 与 `outputs`）。
   *
   * 目前唯一的使用方是 `/video-studio` 的「生成案例片」：它要等这条片子出完，
   * 把产出地址挂回模板（`video-templates` 的 `sampleUrl`）。那种「跑完之后还有
   * 一件收尾动作」的需求，页面自己盯 `jobs` 会踩两个坑 —— 认不出哪条是本次的、
   * 也处理不好「还没入列 / 还在跑」的中间态。这些状态 hook 里本来就在盯，所以
   * 回调从**同一处**发出去，页面不必再写一份判据。
   *
   * ⚠️ 与工作区清理是**两条独立的路**：`onDone` 在 `keepWorkspace` 时照样触发
   * （案例片就不该清工作区），工作区清理则照旧只认 `workspace` + 指纹比对。
   */
  onDone?: (jobs: Job[]) => void
}

/**
 * `useGenerateFlow` 的页面级开关。
 */
export type GenerateFlowOptions = {
  /**
   * **生成成功后自动清空工作区**（2026-10-09 用户口径：「整个项目已经完成的任务
   * 为什么没清空内容，还有上传的图片」）。
   *
   * 它清三样，全在这一条通道里：
   *   ① 左栏表单 —— `reset()`（页面把 useDraft 的 reset 传进来）；
   *   ② 上传的参考图 —— 走 ①（参考图是表单的一部分，`refs` 就在草稿里）；
   *   ③ 右栏「本轮产出」—— 由 hook 用 `clearResults` 收起（`silent`，见那边注释）。
   *
   * ## 为什么不放在 `request` 的载荷里
   *
   * 它必须能读到**当前**的表单值（见下面那条指纹判据），而载荷是**点击那一刻**
   * 拼好的、之后不再更新 —— 放在载荷里，快照就冻结在点击那一刻，指纹比对退化成
   * 「自己等于自己」，永远判「没改过」。所以它是 hook 的**选项**：页面每次渲染
   * 都把最新那份 `workspace` 传进来，hook 存进 ref，读到的才是当前值。
   * 页面写法：`useGenerateFlow({ workspace: { snapshot: form, reset } })`。
   *
   * ## 指纹判据：两次一样才清
   *
   * `confirm` 时取一次指纹（`JSON.stringify(snapshot)`）存下来，任务跑完再取一次。
   * 这一条防的是「把用户正在写的东西当旧内容清掉」：图要跑 60 秒，用户在这 60 秒里
   * 很可能已经开始录入下一个产品 —— 不加指纹比对，那半页输入会在上一轮完成的
   * 瞬间被 `reset()` 抹掉，而且不报错。**用户的改动永远赢**（与 VideoStudio 的
   * `sbInputKey` 同一条判据：手改过就不套模板）。
   *
   * ## 只在「真的出了东西」时清
   *
   * 全部失败时**不清**：失败任务要靠原表单改词重试，把表单清了等于把线索一起清掉。
   */
  workspace?: { snapshot: unknown; reset: () => void }
}

/**
 * 「提交成功后清工作区」这件事**要跨页面重挂载存活**。
 *
 * 2026-10-10 用户口径：「/social-image 完成后没清空信息」。根因不是判据写错，
 * 而是 `track` 只是内存里的 ref —— 任务跑着（图约 60 秒）的时候，用户切到别的页
 * 再切回来（或刷新），页面重挂载、ref 归零，任务落地时就没人认领了：
 * 表单与上传的图一直留在那里，用户看到的是「完成了却没清」。
 *
 * 修法：**把待清批次落盘**，重挂载后按页面路径认领回来。存的是
 * 「页面路径 → { 本批本地任务 id, 提交那一刻的表单指纹 }」——
 * 指纹照旧要带：用户在离开期间改了表单，回来就不许清（见上面 `workspace` 的说明）。
 */
const WS_PENDING_KEY = 'hw_ws_pending_v1'

function readWsPending(): Record<string, { ids: string[]; snap: string }> {
  try {
    const raw = localStorage.getItem(WS_PENDING_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, { ids: string[]; snap: string }>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeWsPending(rec: Record<string, { ids: string[]; snap: string }>): void {
  try {
    localStorage.setItem(WS_PENDING_KEY, JSON.stringify(rec))
  } catch {
    /* 存不下就退回「只在本次挂载内生效」——与原行为一致，不是新错误 */
  }
}

/**
 * 生成前「报价确认」流程。
 * 参考站的关键交互：点生成不直接扣费，先弹一次报价与提示词确认，
 * 确认后才提交。避免误点造成消耗，也让用户看清将发送什么。
 *
 * **批量也是这一次弹窗**：批量确认与单个确认是同一个决策关口（花多少钱、
 * 发什么词），拆成两个弹窗会让报价行、免费标记、提示词展示各维护两遍。
 * 弹窗内按 `info.batch` 自增殖行清单 —— 交互只有一个入口。
 */
export function useGenerateFlow(opts?: GenerateFlowOptions) {
  const { submit, submitBatch, jobs, clearResults } = useJobCenter()
  const [pending, setPending] = useState<GeneratePayload | null>(null)

  /**
   * 工作区（页面每次渲染都重新传一份，所以这里存的永远是**最新那份**）——
   * 见 `GenerateFlowOptions.workspace` 的说明。
   */
  const wsRef = useRef(opts?.workspace)
  wsRef.current = opts?.workspace

  /**
   * 本次提交要盯的一批任务 —— 任务落地后做两件事：跑 `onDone` 回调、按指纹决定要不要清工作区。
   *
   * `snap` 为 `null` 表示这一批**不参与工作区清理**（`keepWorkspace` 的档，如案例片；
   * 或页面没开 `workspace`）—— 但 `onDone` 照常跑。两条路各自独立，见 `onDone` 的注释。
   *
   * 只留**最后一次提交**：用户连点两次「生成」时，前一批的完成不该把第二轮的工作区
   * 也清掉。新的一批一提交就覆盖它 —— 前一批结束时 `track.current` 已指向新批次，
   * 旧批次那一轮自然不再触发清理。
   */
  const track = useRef<{ ids: string[]; snap: string | null; onDone?: (jobs: Job[]) => void } | null>(null)

  /**
   * 页面身份（当前路径）—— 用来落盘 / 认领「待清批次」，
   * 让「任务成功清工作区」在页面**重挂载后仍然生效**（见 `WS_PENDING_KEY`）。
   */
  const pageKey = typeof window !== 'undefined' ? window.location.pathname : ''

  /**
   * 重挂载后认领本页尚未落定的待清批次。`track` 是 ref，重挂载即清空 ——
   * 不认领回来，「切走又切回 / 刷新」这一条路上的自动清空就永远不触发
   * （正是用户报的那个「完成后没清空」）。
   *
   * 认领是幂等的：已有在跑的 `track`（同一次挂载里的新提交）优先，不覆盖。
   */
  useEffect(() => {
    if (track.current) return
    const rec = readWsPending()[pageKey]
    if (rec && Array.isArray(rec.ids) && rec.ids.length && typeof rec.snap === 'string') {
      track.current = { ids: rec.ids, snap: rec.snap, onDone: undefined }
    }
  }, [pageKey])

  const request = useCallback((payload: GeneratePayload) => setPending(payload), [])
  const cancel = useCallback(() => setPending(null), [])
  /**
   * 确认提交。
   *
   * `edits` = 用户在确认弹窗里**改过的提示词**（`PromptEdits`）。它必须在这里
   * 并回 `args` —— 弹窗里能编辑、发出去的却是模板原文的话，用户改的那一版
   * 等于白改，而且看不出来（预览与实发不是同一份，本项目最不该破的一条）。
   *
   * ⚠️ 编辑**只对单条提交生效**。弹窗里可编辑的两支都是「单产品」形态；
   * 批量那一支是只读的（一行是「该行全部张拼起来的词」，改一处没法回填成 N 张，
   * 见 `PriceConfirmDialog`）。所以 `list.length > 1` 时 `edits` 必然是 `undefined`。
   * 这里仍显式挡住另一种可能：万一以后批量也可编辑、而这里忘了改，
   * **一份词会被抄到每一行** —— 提交、出图、记账全都正常，只有图不对。
   */
  const confirm = useCallback(
    async (edits?: PromptEdits) => {
      const p = pending
      setPending(null)
      if (!p) return
      const list = Array.isArray(p.args) ? p.args : [p.args]
      if (!list.length) return
      const args = edits && list.length === 1 ? [withPromptEdits(list[0], edits)] : list
      // 长度 1 → 走单提交路径（**不生成批次 id**，右栏就不会把它聚成「批量 · 1 个」）；
      // 长度 > 1 → 走批次路径，共用一个批次 id，逐行串行提交、失败不连坐。
      //
      // 这里收下主任务的**本地 id**：附带任务要挂在它下面（见下面那段），
      // 而 `submit` 的返回正是那个 id（它在提交前就生成了，失败也照旧返回）。
      const mainIds =
        args.length === 1 ? [await submit(args[0])] : await submitBatch(args)
      /*
       * 附带任务（见 `also` 上的说明）：主任务提交完再逐条提交。
       *
       * ⚠️ 顺序是**主任务在前**，不是并发：主任务失败（`state: 'failed'`）时
       * 附带任务照旧提交 —— 两条本来就不互相依赖（图文各自独立），
       * 但按主任务的价格 / 参数是用户刚刚核对过的那一份，
       * 「先发核对过的」这个顺序让人回头查记录时对得上。
       *
       * ⚠️ **挂靠点在这里补**（`attachedTo: mainIds[0]`），不在页面上补：
       * 页面的 `copyArgs` 是提交**之前**拼好的，那时主任务的 id 还不存在 ——
       * 让页面去猜 / 去占位，等于把「本地 id 什么时候生成」这件事写给页面看。
       * 拿到 id 的只有这里（`submit` 的返回值），所以只有这里能挂。
       *
       * ⚠️ 它**只影响右栏那张卡**（把图文聚成一张、点一次就能看），
       * 两条任务本身照旧各自计费 / 各自成败 / 各自重试。
       */
      const attached = (p.also ?? []).map((extra) => ({ ...extra, attachedTo: mainIds[0] }))
      const extraIds: string[] = []
      for (const extra of attached) extraIds.push(await submit(extra))

      /*
       * 自动清空工作区：记下**这一批**的本地 id 与提交那一刻的表单指纹。
       * `keepWorkspace` 的档（分镜图 / 配音这类辅助产物）不记 —— 它们成功时
       * 不该把主表单清掉。见 payload 上那个字段的说明。
       * 有 `onDone` 时**即便 keepWorkspace 也要记**（`snap` 给 null）：回调不等工作区清理。
       */
      const ws = p.keepWorkspace ? undefined : wsRef.current
      track.current =
        ws || p.onDone
          ? { ids: [...mainIds, ...extraIds], snap: ws ? JSON.stringify(ws.snapshot) : null, onDone: p.onDone }
          : null
      /*
       * 落盘「待清批次」，让页面重挂载后仍能认领（见 `WS_PENDING_KEY`）。
       * 只有参与工作区清理的那一档才记 —— `keepWorkspace` 的辅助产物不该清主表单。
       */
      if (ws && track.current?.snap) {
        const rec = readWsPending()
        rec[pageKey] = { ids: track.current.ids, snap: track.current.snap }
        writeWsPending(rec)
      }
    },
    [pending, submit, submitBatch, pageKey],
  )

  /**
   * 盯住上面那一批任务：**全部落地**后先跑 `onDone`，再按指纹决定要不要清工作区。
   *
   * 三个「还不能下结论」的早退一个都不能省（各自防的坑见 `workspace` 字段上的注释）：
   *   · 有条目还没进列表 —— 提交与入列之间有一拍，这时不能下结论；
   *   · 还有在跑的 —— 图 60 秒、文案 12 秒，先回来的那条不能替整批做决定。
   *
   * ⚠️ `ws.snapshot` 取的是 `wsRef.current`（**当前**表单值），不是提交那一刻冻结的
   * 那一份 —— 所以这里比的确实是「提交之后用户动没动过」。而工作区清理要求
   * 「这一批真的出了东西」（全失败不清：用户还要照着表单改词重试）；`onDone`
   * 则无论成败都发 —— 它要的是「结束了」这个事实（案例片失败时也要告诉页面）。
   */
  useEffect(() => {
    const t = track.current
    if (!t) return
    const byId = new Map(jobs.map((j) => [j.id, j]))
    const tracked = t.ids.map((id) => byId.get(id))
    if (tracked.some((j) => !j)) return
    if (tracked.some((j) => j && isActive(j.state))) return
    // 这一批已经尘埃落定，别再重复触发
    track.current = null
    /*
     * 落盘的「待清批次」跟着失效 —— 无论这一轮最终清不清，都不留给下一轮。
     * 按 id 比对再删：用户可能已经又提交了新一批（`rec[pageKey]` 已指向新批次），
     * 不比对就会把新批次一起误删。
     */
    const rec = readWsPending()
    const saved = rec[pageKey]
    if (saved && JSON.stringify(saved.ids) === JSON.stringify(t.ids)) {
      delete rec[pageKey]
      writeWsPending(rec)
    }
    const done = tracked.filter((j): j is Job => !!j)
    t.onDone?.(done)
    if (!t.snap) return // 不参与工作区清理（keepWorkspace / 没开 workspace）
    const ws = wsRef.current
    if (!ws) return
    if (!tracked.some((j) => j && (j.state === 'succeeded' || j.state === 'partial'))) return
    if (JSON.stringify(ws.snapshot) !== t.snap) return
    ws.reset()
    clearResults(t.ids, { silent: true })
  }, [jobs, clearResults, pageKey])

  return { pending: pending?.info ?? null, request, confirm, cancel }
}
